import { TypedDocumentNode, gql } from '@apollo/client';
import { faAngleDown, faAngleUp } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { MessageDescriptor, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import useToggle from '@sorare/core/src/hooks/useToggle';

import { Game } from '@sorare/us-sports/src/components/game';
import { DecimalScore } from '@sorare/us-sports/src/components/scores/DecimalScore';
import StatsTable from '@sorare/us-sports/src/components/stats/StatsTable';
import { calculateScoreWithBonus } from '@sorare/us-sports/src/lib/sports';

import { StatusRow } from 'components/game/StatusRow';

import {
  GameStatsCard_nbaGame,
  NBAGameStatsCard_gameStats,
} from './__generated__/index.graphql';

type NBAGameStatsCard_gameStats_detailedScores = NonNullable<
  NBAGameStatsCard_gameStats['detailedScores']
>;

type NBAGameStatsCard_gameStats_detailedStats = NonNullable<
  NBAGameStatsCard_gameStats['detailedStats']
>;

const StaticCard = styled(Vertical).attrs({ gap: 0 })`
  border-radius: var(--unit);
  background-color: var(--c-neutral-300);
`;

const ExpandableCard = styled(StaticCard)`
  cursor: pointer;
`;

const Statistics = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
`;

const ToggleButton = styled(Horizontal).attrs({ center: true })`
  font-size: calc(1.5 * var(--unit));
  color: var(--c-neutral-800);
`;

const messages = defineMessages({
  showAllStats: {
    id: 'NBAGameStatsCard.showAllStats',
    defaultMessage: 'Show all statistics',
  },
  hideStats: {
    id: 'NBAGameStatsCard.hideStats',
    defaultMessage: 'Hide statistics',
  },
  totalScore: {
    id: 'NBAGameStatsCard.totalScore',
    defaultMessage: 'Total score',
  },
  bonusPoints: {
    id: 'NBAGameStatsCard.bonusPoints',
    defaultMessage: 'Bonus points ({bonus})',
  },
  fantasyPoints: {
    id: 'NBAGameStatsCard.fantasyPoints',
    defaultMessage: 'Fantasy points',
  },
});

const statLabels: Record<string, MessageDescriptor> = defineMessages({
  points: {
    id: 'NBAGameStatsCard.stats.points',
    defaultMessage: 'Points ({count})',
  },
  rebounds: {
    id: 'NBAGameStatsCard.stats.rebounds',
    defaultMessage: 'Rebounds ({count})',
  },
  assists: {
    id: 'NBAGameStatsCard.stats.assists',
    defaultMessage: 'Assists ({count})',
  },
  blocks: {
    id: 'NBAGameStatsCard.stats.blocks',
    defaultMessage: 'Blocks ({count})',
  },
  steals: {
    id: 'NBAGameStatsCard.stats.steals',
    defaultMessage: 'Steals ({count})',
  },
  turnovers: {
    id: 'NBAGameStatsCard.stats.turnovers',
    defaultMessage: 'Turnovers ({count})',
  },
  made3PointFGs: {
    id: 'NBAGameStatsCard.stats.made3PointFGs',
    defaultMessage: 'Made 3-point FGs ({count})',
  },
  doubleDoubles: {
    id: 'NBAGameStatsCard.stats.doubleDoubles',
    defaultMessage: 'Double doubles ({count})',
  },
  tripleDoubles: {
    id: 'NBAGameStatsCard.stats.tripleDoubles',
    defaultMessage: 'Triple doubles ({count})',
  },
});

type Props = {
  score: number | null;
  bonus: number;
  showBonus?: boolean;
  detailedStats?: NBAGameStatsCard_gameStats_detailedStats | null;
  detailedScores?: NBAGameStatsCard_gameStats_detailedScores | null;
  game: GameStatsCard_nbaGame;
};

type DetailedScoresKey = keyof NBAGameStatsCard_gameStats_detailedScores;

type StatRow = {
  stat: string;
  label: ReactNode | string;
  score: ReactNode | number;
};

const GameStatsCard = (props: Props) => {
  const { formatCardBonus, formatMessage } = useIntlContext();
  const [expanded, toggleExpanded] = useToggle(true);
  const {
    game,
    score,
    detailedStats = null,
    detailedScores = null,
    bonus,
    showBonus = true,
  } = props;

  const { status, startDate, quarter, isHalftime } = game;

  const fantasyStatRows: StatRow[] = Object.entries(detailedStats || [])
    .filter(([key]) => key !== '__typename')
    .map(([stat, rawStat]) => {
      const statKey = stat as DetailedScoresKey;
      const detailedScore = parseFloat(
        (detailedScores?.[statKey] as string) || '0'
      );
      return {
        stat,
        label: statLabels[stat]
          ? formatMessage(statLabels[stat], { count: rawStat })
          : stat,
        score: <DecimalScore value={detailedScore} />,
      };
    });

  const Card = detailedStats ? ExpandableCard : StaticCard;
  const scoreWithBonus =
    score !== null && calculateScoreWithBonus(score, bonus);
  return (
    <Card
      key={game.id}
      onClick={detailedStats ? () => toggleExpanded() : undefined}
    >
      <Game
        game={game}
        StatusRow={
          <StatusRow
            status={status}
            startDate={startDate}
            quarter={quarter}
            isHalftime={isHalftime}
            cardScore={
              scoreWithBonus !== false ? scoreWithBonus.score : undefined
            }
          />
        }
      />
      {detailedStats && scoreWithBonus && (
        <Statistics>
          {expanded && (
            <>
              {showBonus && (
                <StatsTable
                  headerTitle={formatMessage(messages.totalScore)}
                  headerScore={scoreWithBonus.score}
                  rows={[
                    {
                      stat: 'bonusPoints',
                      label: formatMessage(messages.bonusPoints, {
                        bonus: formatCardBonus(bonus),
                      }),
                      score: <DecimalScore value={scoreWithBonus.bonus} />,
                    },
                  ]}
                />
              )}
              <StatsTable
                headerTitle={formatMessage(messages.fantasyPoints)}
                headerScore={score}
                rows={fantasyStatRows}
              />
              <ToggleButton>
                <FontAwesomeIcon icon={faAngleUp} />
                {formatMessage(messages.hideStats)}
              </ToggleButton>
            </>
          )}
          {!expanded && (
            <ToggleButton>
              <FontAwesomeIcon icon={faAngleDown} />
              {formatMessage(messages.showAllStats)}
            </ToggleButton>
          )}
        </Statistics>
      )}
    </Card>
  );
};

GameStatsCard.fragments = {
  gameStats: gql`
    fragment NBAGameStatsCard_gameStats on NBAPlayerGameStats {
      detailedStats {
        points
        rebounds
        assists
        blocks
        steals
        turnovers
        made3PointFGs
        doubleDoubles
        tripleDoubles
      }
      detailedScores {
        points
        rebounds
        assists
        blocks
        steals
        turnovers
        made3PointFGs
        doubleDoubles
        tripleDoubles
      }
    }
  ` as TypedDocumentNode<NBAGameStatsCard_gameStats>,
  nbaGame: gql`
    fragment GameStatsCard_nbaGame on NBAGame {
      id
      status
      startDate
      homeScore
      awayScore
      quarter
      isHalftime
      homeTeam {
        id
        slug
        name
        svgUrl
      }
      awayTeam {
        id
        slug
        name
        svgUrl
      }
    }
  ` as TypedDocumentNode<GameStatsCard_nbaGame>,
};

export default GameStatsCard;
